import * as React from "react";
import { cn } from "@/lib/utils";
import { GradientText, SectionLabel } from "./GradientText";
import { Play } from "lucide-react";

interface AppDemoVideoProps {
  title: string;
  description?: string;
  videoId: string;
  thumbnail: string;
}

export function AppDemoVideo({
  title,
  description,
  videoId,
  thumbnail,
}: AppDemoVideoProps) {
  const [isVisible, setIsVisible] = React.useState(false);
  const [isPlaying, setIsPlaying] = React.useState(false);
  const sectionRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry?.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const handlePlay = () => {
    setIsPlaying(true);
  };

  return (
    <section ref={sectionRef} className="py-24 lg:py-32">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section header */}
        <div
          className={cn(
            "text-center max-w-3xl mx-auto mb-16 lg:mb-20",
            "transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)]",
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          )}
        >
          <SectionLabel className="mb-6">Demo</SectionLabel>

          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight mb-6">
            <GradientText>{title}</GradientText>
          </h2>

          {description && (
            <p className="text-muted-foreground text-base lg:text-lg leading-relaxed">
              {description}
            </p>
          )}
        </div>

        {/* Video container */}
        <div
          className={cn(
            "relative max-w-4xl mx-auto",
            "transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)]",
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          )}
          style={{ transitionDelay: isVisible ? "200ms" : "0ms" }}
        >
          <div
            className={cn(
              "relative aspect-video rounded-2xl overflow-hidden",
              "bg-muted",
              "border border-border",
              "shadow-lg"
            )}
          >
            {isPlaying ? (
              <iframe
                src={`https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0`}
                title={title}
                className="absolute inset-0 w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            ) : (
              <>
                {/* Thumbnail */}
                <div className="absolute inset-0 bg-muted">
                  <img
                    src={thumbnail}
                    alt={title}
                    className="w-full h-full object-cover opacity-90"
                    onError={(e) => {
                      e.currentTarget.style.display = "none";
                    }}
                  />
                  {/* Gradient overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
                </div>

                {/* Play button */}
                <button
                  onClick={handlePlay}
                  className={cn(
                    "absolute inset-0 flex items-center justify-center",
                    "group cursor-pointer"
                  )}
                  aria-label="Play video"
                >
                  <div
                    className={cn(
                      "w-20 h-20 rounded-full flex items-center justify-center",
                      "bg-accent text-white",
                      "shadow-lg shadow-accent/30",
                      "transition-all duration-300",
                      "group-hover:scale-110 group-hover:shadow-xl group-hover:shadow-accent/40"
                    )}
                  >
                    <Play className="w-8 h-8 ml-1" fill="currentColor" />
                  </div>
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
